import React, { useState, useEffect, useContext, useCallback } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { FriendsContext } from '../contexts/FriendsContext';
import { ResultsContext } from '../contexts/ResultsContext';
import './TournamentOverallActions.css';
import {useTournamentAppConfiguration} from "../utils/tournamentconfig/TournamentConfiguration"
import {calculateTournamentRankings} from "../utils/tournamentScoring"
import {filterIgnoredGames} from "../utils/ignoredGames"

/**
 * TournamentOverallActions component displays overall rankings for all players in a tournament
 * 
 * @param {Object} props - Component props
 * @param {string} props.tournamentId - Tournament ID to display rankings for
 * @param {string} props.currentUsername - Current username being viewed
 */
function TournamentOverallActions({ tournamentId, currentUsername }) {
  const tournamentAppConfiguration = useTournamentAppConfiguration()

  const { theme } = useContext(ThemeContext);
  const { isFriend, addFriend, removeFriend } = useContext(FriendsContext);
  const { results, loading, error } = useContext(ResultsContext);
  const [players, setPlayers] = useState([]);
  const [showAllRankings, setShowAllRankings] = useState(false);
  const [showFriendsOnly, setShowFriendsOnly] = useState(false);

  // Process tournament data and calculate player rankings using centralized function
  const processResults = useCallback(() => {
    if (!results || !results.games) return;
    
    const games = filterIgnoredGames(results.games, tournamentId);
    
    // Get all unique players from all games
    const allPlayers = new Set();
    games.forEach(game => {
      if (game.leaderboard) {
        game.leaderboard.forEach(entry => allPlayers.add(entry.userName));
      }
    });
    
    // Calculate rankings for each player and build the players array
    const playersArray = Array.from(allPlayers).map(playerName => {
      const tournamentData = calculateTournamentRankings(games, tournamentAppConfiguration, playerName, '-');
      
      // Calculate average rank and games played
      let totalRank = 0;
      let gamesPlayed = 0;
      
      games.forEach(game => {
        const userEntry = game.leaderboard?.find(entry => 
          entry.userName.toLowerCase() === playerName.toLowerCase()
        );
        if (userEntry) {
          totalRank += userEntry.rank;
          gamesPlayed++;
        }
      });
      
      return {
        name: playerName,
        initials: Array.from(tournamentData.initialsUsed).join(', '),
        totalPoints: tournamentData.totalPoints,
        gamesPlayed,
        averageRank: gamesPlayed > 0 ? totalRank / gamesPlayed : 0,
        rank: tournamentData.overallRank
      };
    });
    
    // Sort by rank (overall rank is already calculated in the centralized function)
    playersArray.sort((a, b) => (a.rank || Infinity) - (b.rank || Infinity));
    
    setPlayers(playersArray);
  }, [results, tournamentAppConfiguration, tournamentId]);

  // Process results when they change
  useEffect(() => {
    processResults();
  }, [results, processResults]);

  // Handle player search
  const handlePlayerSearch = (playerName) => {
    if (!playerName) return;
    
    // Save to localStorage
    localStorage.setItem('lastUsername', playerName);
    
    // Update URL parameters
    const url = new URL(window.location);
    url.searchParams.set('username', playerName);
    if (tournamentId) {
      url.searchParams.set('tournamentId', tournamentId);
    }
    window.history.replaceState({}, '', url);
    
    // Directly update the current username in the parent component
    // This will trigger a re-render with the new username
    if (typeof window.updateSearchUsername === 'function') {
      window.updateSearchUsername(playerName, tournamentId);
    }
  };

  // Handle friend button click
  const handleFriendButtonClick = (username) => {
    if (isFriend(username)) {
      removeFriend(username);
    } else {
      addFriend(username);
    }
  };

  // Filter players based on current settings
  const getFilteredPlayers = () => {
    if (showFriendsOnly) {
      return players.filter(player => 
        isFriend(player.name) || 
        (currentUsername && player.name.toLowerCase() === currentUsername.toLowerCase())
      );
    }
    return players;
  };

  // Get displayed players based on showAllRankings flag
  const getDisplayedPlayers = () => {
    const filteredPlayers = getFilteredPlayers();
    
    if (showAllRankings) {
      return filteredPlayers;
    } else {
      // Get top 10 players
      const top10 = filteredPlayers.slice(0, 10);
      
      // Check if current user is in top 10
      const currentUserInTop10 = currentUsername && top10.some(
        player => player.name.toLowerCase() === currentUsername.toLowerCase()
      );
      
      // If current user is not in top 10, add them to the displayed players
      if (!currentUserInTop10 && currentUsername) {
        const currentUserIndex = filteredPlayers.findIndex(
          player => player.name.toLowerCase() === currentUsername.toLowerCase()
        );
        
        if (currentUserIndex !== -1) {
          return [...top10, filteredPlayers[currentUserIndex]];
        }
      }
      
      return top10;
    }
  };

  if (!tournamentId) {
    return null;
  }

  if (loading) {
    return <div className={`loading ${theme}`}>Loading rankings data...</div>;
  }

  if (error) {
    return <div className={`error ${theme}`}>{error}</div>;
  }

  const displayedPlayers = getDisplayedPlayers();
  const filteredPlayers = getFilteredPlayers();

  return (
    <div className={`tournament-overall-actions ${theme}`}>
      <div className="tournament-rankings-header">
        <div className="tournament-rankings-title">
          {showFriendsOnly ? 'Friends Rankings' : 'Tournament Overall Rankings'}
        </div>
        
        <div className="toggle-buttons-container">
          <button 
            className="toggle-rankings-button"
            onClick={() => setShowAllRankings(!showAllRankings)}
          >
            {showAllRankings ? 'Show Top 10' : 'Show All'}
          </button>
          
          <button 
            className={`toggle-rankings-button ${showFriendsOnly ? 'active' : ''}`}
            onClick={() => setShowFriendsOnly(!showFriendsOnly)}
          >
            {showFriendsOnly ? 'Show All Players' : 'Show Friends Only'}
          </button>
        </div>
      </div>
      
      {showFriendsOnly && filteredPlayers.length === 0 && (
        <div className="no-friends-message">
          <p>You haven't added any friends yet.</p>
          <p>Click the "Add Friend" button next to players in the overall rankings to add them to your friends list.</p>
          <button 
            className="toggle-rankings-button"
            onClick={() => setShowFriendsOnly(false)}
          >
            Show All Players
          </button>
        </div>
      )}
      
      {filteredPlayers.length > 0 && (
        <div className="table-responsive">
          <table className="rankings-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Player</th>
                {tournamentAppConfiguration.showInitials && <th>Initials</th>}
                <th>Games Ranked</th>
                <th>Total Points</th>
                <th>Avg. Rank</th>
                <th>Friend</th>
              </tr>
            </thead>
            <tbody>
              {displayedPlayers.map((player) => (
                <tr 
                  key={player.name}
                  className={currentUsername && player.name.toLowerCase() === currentUsername.toLowerCase() ? 'highlighted-player' : ''}
                >
                  <td className="player-rank">
                    {player.rank > tournamentAppConfiguration.cutoffRank ? (
                      <span>
                        #{player.rank} <span className="cutoff-indicator" title="Below top 50 cutoff">⚠️</span>
                      </span>
                    ) : (
                      `#${player.rank}`
                    )}
                  </td>
                  <td>
                    {currentUsername && player.name.toLowerCase() === currentUsername.toLowerCase() ? (
                      player.name
                    ) : (
                      <button 
                        className="player-link-button"
                        onClick={() => handlePlayerSearch(player.name)}
                        title={`Search for ${player.name}`}
                      >
                        {player.name}
                      </button>
                    )}
                  </td>
                  {tournamentAppConfiguration.showInitials && <td>{player.initials}</td>}
                  <td>{player.gamesPlayed}</td>
                  <td>{player.totalPoints}</td>
                  <td>{player.averageRank.toFixed(2)}</td>
                  <td>
                    {currentUsername && player.name.toLowerCase() === currentUsername.toLowerCase() ? (
                      <span>(You)</span>
                    ) : (
                      <button 
                        className={`friend-button ${isFriend(player.name) ? 'remove-friend' : 'add-friend'}`}
                        onClick={() => handleFriendButtonClick(player.name)}
                        title={isFriend(player.name) ? 'Remove friend' : 'Add friend'}
                      >
                        {isFriend(player.name) ? '✓' : '+'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {!showAllRankings && filteredPlayers.length > 10 && (
        <div className="hidden-players-message">
          {filteredPlayers.length - 10} more players not shown. Click <button 
            className="show-all-link" 
            onClick={() => setShowAllRankings(true)}
          >
            Show All
          </button> to see all players.
        </div>
      )}
      
      {filteredPlayers.some(player => player.rank > tournamentAppConfiguration.cutoffRank) && (
        <div className="cutoff-explanation">
          <div className="cutoff-info">
            <span className="cutoff-indicator">⚠️</span>
            <span>Players with ranks below {tournamentAppConfiguration.cutoffRank} are marked with this indicator as they are below the tournament cutoff.</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentOverallActions;